import React, { useCallback, useEffect, useRef } from 'react';
import { Mic, MicOff } from 'lucide-react';
import { useTranscriptStore } from '../store/transcriptStore';
import { useAuthStore } from '../store/authStore';
import { saveTranscription } from '../lib/supabase';

export const RecordButton: React.FC = () => {
  const { isRecording, setIsRecording, appendTranscript } = useTranscriptStore();
  const { isAuthenticated } = useAuthStore();
  const recognitionRef = useRef<SpeechRecognition | null>(null);

  const handleAuthPrompt = () => {
    const message = 'To use voice dictation, you need to:\n\n' +
      '1. Sign in to this dictation app\n' +
      '2. Sign in to the notewriter app\n\n' +
      'Would you like to sign in now?';

    if (window.confirm(message)) {
      // Redirect to login page or show login modal
      window.location.reload();
    }
  };

  const setupRecognition = useCallback(() => {
    if (!isAuthenticated) {
      handleAuthPrompt();
      return;
    }

    if (!('webkitSpeechRecognition' in window)) {
      alert('Speech recognition is not supported in this browser.');
      return;
    }

    const SpeechRecognition = window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onresult = async (event) => {
      const result = event.results[event.results.length - 1];
      if (result.isFinal) {
        const transcript = result[0].transcript;
        appendTranscript(transcript);
        try {
          await saveTranscription(transcript);
        } catch (error) {
          console.error('Failed to save transcription:', error);
        }
      }
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsRecording(false);
    };

    recognition.onend = () => {
      if (isRecording) {
        recognition.start();
      }
    };

    recognitionRef.current = recognition;
  }, [appendTranscript, setIsRecording, isRecording, isAuthenticated]);

  useEffect(() => {
    setupRecognition();
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, [setupRecognition]);

  const toggleRecording = () => {
    if (!isAuthenticated) {
      handleAuthPrompt();
      return;
    }

    if (!recognitionRef.current) {
      setupRecognition();
    }

    if (!isRecording) {
      recognitionRef.current?.start();
    } else {
      recognitionRef.current?.stop();
    }
    setIsRecording(!isRecording);
  };

  return (
    <button
      onClick={toggleRecording}
      className={`btn-record w-20 h-20 rounded-full shadow-lg ${
        isRecording 
          ? 'bg-red-500 hover:bg-red-600' 
          : 'bg-blue-500 hover:bg-blue-600'
      }`}
      aria-label={isRecording ? 'Stop Recording' : 'Start Recording'}
    >
      {isRecording ? (
        <MicOff className="w-10 h-10 text-white mx-auto" />
      ) : (
        <Mic className="w-10 h-10 text-white mx-auto" />
      )}
    </button>
  );
};